import { useEffect, useState } from 'react';
import { SessionStore } from './session-store';
import auth0, { Auth0DecodedHash, Auth0Error, AuthOptions, WebAuth } from 'auth0-js';
import { TransactionManager } from './transaction-manager';
import { getLocation } from '../util';
import { buildQueryString } from '../util/strings';

export const authenticationExpirationStorageKey = 'auth_exp';
export const authenticationTokenStorageKey = 'auth_token';
export const authenticationUserStorageKey = 'auth_user';
export const authenticationHashStorageKey = 'auth_hash';

/**
 * Get whether the application is authenticated, i.e. has a token which is not expired yet
 */
export const isAuthenticated = (): boolean => {
    const storedAuthenticationExpiration = SessionStore.get(authenticationExpirationStorageKey);
    if (storedAuthenticationExpiration === null) {
        return false;
    }
    const expiration = JSON.parse(storedAuthenticationExpiration);
    return new Date().getTime() < expiration;
};

/**
 * Get the current authentication token
 * @param unsafe Returns a token/whether a token exists independently of the expiration flag
 */
export const getAuthenticationToken = (unsafe = false): string|null => {
    if (isAuthenticated() || unsafe) {
        const token = SessionStore.get(authenticationTokenStorageKey);
        if (token) {
            return token;
        }
    }
    return null;
};

/**
 * Get the auth0 user ID of the current user if authenticated
 */
export const getAuthenticatedUserId = (): string|null => {
    if (isAuthenticated()) {
        return SessionStore.get(authenticationUserStorageKey);
    }
    return null;
};

const handleAuthResult = (authResult: Auth0DecodedHash, authenticationExtensionHandler: ReturnType<typeof extendSession>): void => {
    SessionStore.put(authenticationExpirationStorageKey, JSON.stringify(
        authResult.expiresIn * 1000 + new Date().getTime()
    ));
    SessionStore.put(authenticationTokenStorageKey, authResult.accessToken);
    if (authResult.idTokenPayload && authResult.idTokenPayload.sub) {
        SessionStore.put(authenticationUserStorageKey, authResult.idTokenPayload.sub);
    }
    if (authenticationExtensionHandler) {
        window.setTimeout(authenticationExtensionHandler, 15 * 60 * 1000);
    }
};

const handleAccessHash = (
    webAuth: WebAuth,
    onError: ((err: Auth0Error) => void)|null = null,
    autoRenewSession = true
) => async (accessHash: string) => {
    if (accessHash) {
        SessionStore.put(authenticationHashStorageKey, accessHash);
        const stateFromHash = accessHash.match(/state=([^&]+)/);
        let state: string;
        let nonce: string;
        if (stateFromHash) {
            state = stateFromHash[1];
            [ state, nonce ] = TransactionManager.getTransaction(stateFromHash[1]);
        } else {
            [ state, nonce ] = TransactionManager.getTransaction();
        }
        const authResult = await new Promise<Auth0DecodedHash>((res, rej) => {
            webAuth.parseHash({ hash: accessHash, state, nonce }, (err, authResult) => {
                if (err) {
                    rej(err);
                } else {
                    res(authResult);
                }
            });
        })
            .catch(e => {
                if (onError) {
                    onError(e);
                }
                SessionStore.delete(authenticationHashStorageKey);
            });
        if (authResult) {
            handleAuthResult({
                accessToken: authResult.accessToken,
                expiresIn: authResult.expiresIn,
                idTokenPayload: authResult.idTokenPayload,
            }, autoRenewSession ? extendSession(webAuth, onError) : null);
            return authResult.accessToken;
        } else {
            return null;
        }
    } else {
        return null;
    }
};

export const extendSession = (
    webAuth: WebAuth,
    onError: ((err: Auth0Error) => void)|null = null,
    autoRenewSession = true,
    requireAuthenticatedApp = true
) => (): Promise<void> => {
    if (!requireAuthenticatedApp || isAuthenticated()) {
        return new Promise((res, rej) => {
            webAuth.checkSession({ prompt: 'none' }, (err, data) => {
                if (err) {
                    if (onError) {
                        onError(err);
                    }
                    rej();
                } else {
                    handleAuthResult(data, autoRenewSession ? extendSession(webAuth, onError) : null);
                    res();
                }
            });
        });
    } else {
        return Promise.reject();
    }
};

type UseAuth0InitResponse = {
    loading: boolean;
    webAuth: WebAuth;
    accessToken: string|null;
}

/**
 * @param webAuthConfig
 * @param onError
 * @param autoRenewSession
 * @param useExistingToken Tries to authenticate using a stored token
 */
export const useAuth0Init = (
    webAuthConfig: AuthOptions,
    onError: ((err: Auth0Error) => void)|null = null,
    autoRenewSession = true,
    useExistingToken = false
): UseAuth0InitResponse => {
    const [ loading, setLoading ] = useState(false);
    const webAuth = new auth0.WebAuth(webAuthConfig);
    let accessToken = null;

    useEffect(() => {
        setLoading(true);
        let accessHash = '';
        if (document.location.hash.startsWith('#access_token')) {
            accessHash = document.location.hash;
            (async () => {
                const parseHash = handleAccessHash(webAuth, onError, autoRenewSession);
                accessToken = await parseHash(accessHash);
                setLoading(false);
            })();
            document.location.hash = '';
        } else if (isAuthenticated() && useExistingToken) {
            accessToken = getAuthenticationToken();
            setLoading(false);
        } else if (document.location.hash.startsWith('#error')) {
            const params = new URLSearchParams(document.location.hash.substring(1));
            onError({
                error: params.get('error'),
                errorDescription: params.get('error_description') || undefined,
                error_description: params.get('error_description') || undefined,
            });
        } else {
            setLoading(false);
        }
    }, []);

    return {
        loading,
        webAuth,
        accessToken,
    };
};

export const getLoginUri = (options: Record<string, string> = {}): string => {
    const [ state, nonce ] = TransactionManager.createTransaction();
    const loginArguments = {
        response_type: 'token id_token',
        redirect_uri: encodeURIComponent(getLocation()),
        ...options,
        state,
        nonce,
    };
    return `/?${buildQueryString(loginArguments)}`;
};

export const login = (options: Record<string, string> = {}): void => {
    window.location.href = getLoginUri(options);
};

export const logout = (webAuth: WebAuth) =>
    (returnToUrl = null): void => {
        SessionStore.delete(authenticationExpirationStorageKey);
        SessionStore.delete(authenticationHashStorageKey);
        SessionStore.delete(authenticationTokenStorageKey);
        SessionStore.delete(authenticationUserStorageKey);
        if (returnToUrl) {
            webAuth.logout({
                returnTo: returnToUrl,
            });
        }
    };
